/**
 * Ticket result lines (#674, #748): what the "your orders" list under the ticket says for each submitted order.
 *
 * The /exchange response gives the first line (resting oid, immediate fill with size + average price, or HL's error
 * text). WS orderUpdates then move the line on, but never lose information (#748):
 *   - an order that already filled in the submit response is final: later `open` / `filled` events for its oid
 *     (HL sends both for a crossing IOC, before or after the response) leave "Filled 10 @ 55¢ avg (oid …)" as is;
 *   - a resting order's `open` with a smaller remaining size reads "Partially filled 4 of 10 @ 50¢, rest on the book";
 *   - its `filled` reads "Filled 10 @ 50¢ (oid …)" (a resting order fills at its limit price);
 *   - canceled / rejected keep the filled part and the reason.
 */
import { describeStatus } from '../account/orders/model';
import type { OrderUpdate } from '../hl/types';
import { formatShares, pxToCents } from '../market/format';
import type { OrderStatusResult } from './order';
import { t } from '../i18n/i18n';

export type EntryState = 'open' | 'filled' | 'canceled' | 'rejected' | 'error' | string;

export interface EntryStatus {
  state: EntryState;
  oid?: number;
  detail: string;
}

/** First line, from the /exchange response. */
export function statusDetail(st: OrderStatusResult): EntryStatus {
  if (st.kind === 'resting') return { state: 'open', oid: st.oid, detail: t('ticket.status.resting', { oid: String(st.oid) }) };
  if (st.kind === 'filled') {
    const oid = st.oid !== undefined ? ` ${t('ticket.status.oid_suffix', { oid: String(st.oid) })}` : '';
    return { state: 'filled', oid: st.oid, detail: `${t('ticket.status.filled_avg', { sz: formatShares(st.totalSz), px: pxToCents(st.avgPx) })}${oid}` };
  }
  return { state: 'error', detail: st.message };
}

const TERMINAL = new Set(['filled', 'canceled', 'rejected', 'error']);

/**
 * Apply one WS orderUpdates event to an entry with the same oid. Returns the same object when nothing changes
 * (so React skips the re-render).
 */
export function applyOrderUpdate<T extends EntryStatus>(en: T, u: OrderUpdate): T {
  if (en.oid !== u.order.oid) return en;
  // Final already (immediate fill in the submit response, or an earlier terminal event): keep its line.
  if (TERMINAL.has(en.state)) return en;
  const o = u.order;
  const orig = Number(o.origSz);
  const left = Number(o.sz);
  const filled = Number.isFinite(orig) && Number.isFinite(left) ? Math.max(0, orig - left) : 0;
  const at = pxToCents(o.limitPx);
  const oid = t('ticket.status.oid_suffix', { oid: String(o.oid) });
  const st = describeStatus(u.status);
  let next: EntryStatus;
  switch (st.tone) {
    case 'open':
      next =
        filled > 0
          ? { state: 'open', oid: o.oid, detail: `${t('ticket.status.partial', { filled: formatShares(filled), orig: formatShares(orig), px: at })} ${oid}` }
          : { state: 'open', oid: o.oid, detail: `${t('ticket.status.resting_short')} ${oid}` };
      break;
    case 'filled':
      next = { state: 'filled', oid: o.oid, detail: `${t('ticket.status.filled', { sz: formatShares(orig), px: at })} ${oid}` };
      break;
    case 'canceled':
    case 'rejected': {
      const part = filled > 0 ? ` · ${t('ticket.status.part_filled', { filled: formatShares(filled), orig: formatShares(orig) })}` : '';
      const why = st.reason && u.status !== 'canceled' ? ` · ${st.reason}` : '';
      next = { state: st.tone, oid: o.oid, detail: `${st.label}${part}${why} ${oid}` };
      break;
    }
    default:
      next = { state: u.status, oid: o.oid, detail: `${st.label} ${oid}` };
  }
  return next.state === en.state && next.detail === en.detail ? en : { ...en, ...next };
}
